import {
  FINANCING_LABELS,
  PURCHASE_PURPOSE_LABELS,
  type BuyerBreakdown,
} from '@performance-crm/shared';
import { useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { fmt } from '@/lib/format';
import { useBuyersReport } from '@/lib/queries';

function Breakdown({
  title,
  rows,
  label = (k) => k,
}: {
  title: string;
  rows: BuyerBreakdown[];
  label?: (k: string) => string;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 && <p className="text-muted-foreground text-sm">Sin datos.</p>}
        <ul className="space-y-1.5 text-sm">
          {rows.slice(0, 8).map((r) => (
            <li key={r.key}>
              <div className="flex justify-between">
                <span>{label(r.key)}</span>
                <span className="text-muted-foreground">
                  {r.count} · {fmt.pct(r.pct)}
                </span>
              </div>
              <div className="bg-muted mt-0.5 h-1.5 rounded">
                <div
                  className="bg-primary h-1.5 rounded"
                  style={{ width: `${Math.round(r.pct * 100)}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

/** Perfil del comprador: medio, distrito, edad, financiamiento, ticket, dormitorios / tipología y finalidad. */
export function BuyersTab({ companyId, projectId }: { companyId: string; projectId?: string }) {
  const [scope, setScope] = useState<'VENTA' | 'TODOS'>('TODOS');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const { data, isLoading } = useBuyersReport({
    companyId,
    projectId,
    scope,
    // Fechas puras: la API las resuelve como días completos de la zona de la empresa (api-comercial-embudo-01).
    from: from || undefined,
    to: to || undefined,
  });
  return (
    <div className="space-y-4">
      <div className="bg-card flex flex-wrap items-center gap-2 rounded-lg border p-3">
        <Select
          value={scope}
          onChange={(e) => setScope(e.target.value as 'VENTA' | 'TODOS')}
          aria-label="Alcance"
          className="h-9 w-56"
        >
          <option value="TODOS">Ventas + separaciones activas</option>
          <option value="VENTA">Solo ventas</option>
        </Select>
        <Input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          aria-label="Desde"
          className="h-9 w-40"
        />
        <Input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          aria-label="Hasta"
          className="h-9 w-40"
        />
      </div>
      {isLoading && <Skeleton className="h-64 w-full" />}
      {data && (
        <>
          <div className="grid gap-3 sm:grid-cols-4" data-testid="buyers-summary">
            {(
              [
                ['Compradores', data.total],
                ['Ventas', data.sales],
                ['Separaciones activas', data.separations],
                [
                  'Ticket promedio',
                  data.avgTicketUsd === null ? '—' : fmt.money(data.avgTicketUsd, 'USD'),
                ],
              ] as const
            ).map(([label, v]) => (
              <Card key={label}>
                <CardContent className="p-4">
                  <p className="text-muted-foreground text-xs uppercase">{label}</p>
                  <p className="text-2xl font-semibold">{v}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <Card>
            <CardHeader>
              <CardTitle>Ticket (USD)</CardTitle>
              <CardDescription>
                Compradores por banda de precio; los montos en soles se convierten a USD.
              </CardDescription>
            </CardHeader>
            <CardContent className="h-56">
              <ResponsiveContainer>
                <BarChart data={data.byTicket}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="key" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="count" name="Compradores" fill="#2563eb" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            <Breakdown title="Medio de captación" rows={data.byMedium} />
            <Breakdown title="Distrito" rows={data.byDistrict} />
            <Breakdown
              title="Edad"
              rows={data.byAge}
              label={(k) => (k === 'SIN_DATO' ? 'Sin dato' : `${k} años`)}
            />
            <Breakdown
              title="Financiamiento"
              rows={data.byFinancing}
              label={(k) => FINANCING_LABELS[k] ?? (k === 'SIN_DATO' ? 'Sin dato' : k)}
            />
            <Breakdown
              title="Dormitorios"
              rows={data.byBedrooms}
              label={(k) => (k === 'SIN_DATO' ? 'Sin dato' : k)}
            />
            <Breakdown
              title="Tipología"
              rows={data.byTypology}
              label={(k) => (k === 'SIN_DATO' ? 'Sin dato' : k)}
            />
            <Breakdown
              title="Finalidad"
              rows={data.byPurpose}
              label={(k) => PURCHASE_PURPOSE_LABELS[k] ?? k}
            />
          </div>
        </>
      )}
    </div>
  );
}
